import React, { type ReactElement, type ReactNode } from 'react';

import { noop } from 'lodash-es';

import SelectField, {
  type SelectFieldProps,
} from '../../../select_field/select_field';
import type { SelectOption } from '../../../select_field/types';
import {
  FormContextConsumer,
  type FormContextType,
} from '../../form_context/form_context';
import formField, {
  type FieldHocProps,
  pickProps,
} from '../../form_field/form_field';
import type { FormFieldEvent } from '../../types';

// Helper to trim props which are coming from the FieldHocProps and should never be picked
// in transformProps.
type TrimFieldProps<T> = Omit<
  T,
  | 'onChange'
  | 'onBlur'
  | 'name'
  | 'value'
  | 'id'
  | 'canClear'
  | 'autoComplete'
  | 'disabled'
  | 'readOnly'
  | 'error'
>;

export type SelectFieldWithoutBlurProps<V, O, IO extends SelectOption> = Omit<
  React.JSX.LibraryManagedAttributes<
    typeof SelectField,
    SelectFieldProps<V, O, IO>
  >,
  'onBlur'
>;

type FormFieldOverriddenProps = {
  onBlur?: (value: string | string[], event: FormFieldEvent) => void;
};

export class SelectFieldBlur<
  V,
  O,
  IO extends SelectOption,
> extends React.Component<
  SelectFieldWithoutBlurProps<V, O, IO> & FormFieldOverriddenProps
> {
  static defaultProps = {
    onChange: noop,
    onBlur: noop,
  };

  render(): ReactNode {
    return (
      <SelectField
        {...this.props}
        onBlur={(event) => this.props.onBlur?.(event.target.value, event)}
      />
    );
  }
}

export const selectComponentProps = [
  'options',
  'isCreatorMode',
  'selectFirstAvailableOption',
  'selectFirstWhenValueMissing',
  'preloadOptions',
  'loadOptions',
  'loadSelectedOptions',
  'isMulti',
  'newOptionValue',
  'valuePrimaryKey',
  'valueComponent',
  'optionComponent',
  'onNewOptionNameSelected',
  'placeholder',
  'creatorText',
  'creatorAction',
  'noOptionsPlaceholder',
  'ignoreIds',
  'plainMultiValues',
  'processOptionValue',
  'processNewOptionValue',
  'filterOption',
  'onFocus',
  'optionSourceToSelect',
  'valueSourceToSelect',
  'valueSelectToSource',
  'hideSelected',
  'isFormDisabled',
  'clearValue',
  'optionsListDelayMs',
  'hideOptionsList',
  'markedValueElements',
  'onRemoteOptionsSet',
  'withCheckboxSelection',
  'warning',
  'emptyOptionsOnEmptyFilter',
  'className',
  'allowDuplicateSelectOptions',
  'enableValueCopyPaste',
  'onValuesCopied',
  'onCopyError',
] as const;

export type TrimmedProps<V, O, IO extends SelectOption> = TrimFieldProps<
  SelectFieldWithoutBlurProps<V, O, IO>
>;
export type PickedProps<V, O, IO extends SelectOption> = Pick<
  TrimmedProps<V, O, IO>,
  (typeof selectComponentProps)[number]
>;

export type SelectFieldWrapperProps<
  V = string,
  O = SelectOption,
  IO extends SelectOption = SelectOption,
> = PickedProps<V, O, IO> &
  FieldHocProps<V | undefined, PickedProps<V, O, IO>> & {
    onChange?: (value: V | undefined, event?: FormFieldEvent) => void;
  };

/**
 * formField is a plain function, so calling it pins SelectFieldBlur's V, O and
 * IO to their constraints and the wrapper's own are lost. Restating them here
 * is what lets the JSX below, and every select_* wrapper that spreads into it,
 * be checked at all.
 */
// biome-ignore lint/plugin: see above.
const Field = formField(SelectFieldBlur, {
  transformProps: (props) => pickProps(props, selectComponentProps),
}) as <V, O, IO extends SelectOption>(
  props: SelectFieldWrapperProps<V, O, IO> & FormContextType,
) => ReactElement;

class SelectFieldWrapper<
  V = string,
  O = SelectOption,
  IO extends SelectOption = SelectOption,
> extends React.Component<SelectFieldWrapperProps<V, O, IO>> {
  render(): ReactNode {
    return (
      <FormContextConsumer>
        {(context) => <Field {...this.props} {...context} />}
      </FormContextConsumer>
    );
  }
}

export default SelectFieldWrapper;
